import type { Address, Device } from "@medplum/fhirtypes";

/** Get status variant for Location/Device display */
export function getResourceStatusVariant(status: string | undefined): "success" | "warning" | "default" | "error" {
  switch (status) {
    case "active":
      return "success";
    case "suspended":
    case "inactive":
      return "warning";
    case "entered-in-error":
      return "error";
    default:
      return "default";
  }
}

/** Format FHIR Address as single-line string */
export function formatAddress(address: Address | undefined): string {
  if (!address) return "";
  const parts = [address.line?.join(", "), address.city, address.state, address.postalCode, address.country].filter(
    Boolean,
  );
  return parts.join(", ");
}

/** Get primary display name from Device.deviceName array */
export function formatDeviceName(device: Device): string {
  if (!device.deviceName?.length) return "Unnamed Device";
  const friendly = device.deviceName.find((n) => n.type === "user-friendly-name");
  return friendly?.name ?? device.deviceName[0]?.name ?? "Unnamed Device";
}

/** Parse Location ID from a FHIR reference string like "Location/abc-123" */
export function parseLocationRef(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const match = ref.match(/^Location\/(.+)$/);
  return match?.[1];
}

/** Location tree node for hierarchy display */
export interface LocationTreeNode {
  location: TreeLocation;
  children: LocationTreeNode[];
  depth: number;
}

/** The two fields the hierarchy is built from — satisfied by `LocationDto`. */
interface TreeLocation {
  locationId: string;
  partOfLocationId?: string;
}

/** Build a flat, depth-ordered list from a Location hierarchy, for display. */
export function buildLocationTree<T extends TreeLocation>(locations: T[]): Array<{ location: T; depth: number }> {
  const byId = new Map(locations.map((l) => [l.locationId, l] as const));
  const childrenMap = new Map<string, T[]>();

  for (const loc of locations) {
    const parentId = loc.partOfLocationId;
    if (parentId) {
      const siblings = childrenMap.get(parentId) ?? [];
      siblings.push(loc);
      childrenMap.set(parentId, siblings);
    }
  }

  const result: Array<{ location: T; depth: number }> = [];
  const visited = new Set<string>();

  function walk(loc: T, depth: number) {
    // A cyclic `partOf` is not forbidden by the server; don't recurse forever.
    if (visited.has(loc.locationId)) return;
    visited.add(loc.locationId);
    result.push({ location: loc, depth });
    for (const child of childrenMap.get(loc.locationId) ?? []) walk(child, depth + 1);
  }

  // Roots: no parent, or a parent outside this set.
  for (const loc of locations) {
    const parentId = loc.partOfLocationId;
    if (!parentId || !byId.has(parentId)) walk(loc, 0);
  }

  return result;
}

/** Get all descendant IDs for a location (for circular reference prevention) */
export function getLocationDescendantIds(
  locationId: string,
  locations: Array<{ locationId: string; partOfLocationId?: string }>,
): Set<string> {
  const descendants = new Set<string>();
  const childrenMap = new Map<string, string[]>();

  for (const loc of locations) {
    const parentId = loc.partOfLocationId;
    if (parentId) {
      const siblings = childrenMap.get(parentId) ?? [];
      siblings.push(loc.locationId);
      childrenMap.set(parentId, siblings);
    }
  }

  function collect(id: string) {
    const children = childrenMap.get(id) ?? [];
    for (const childId of children) {
      descendants.add(childId);
      collect(childId);
    }
  }

  collect(locationId);
  return descendants;
}
